import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../utils/api";

/** Sıradaki randevuyu kabul et - POST /api/appointments/next */
export const acceptNext = createAsyncThunk(
  "appointment/acceptNext",
  async (departmentId) => {
    const response = await api.post("/api/appointments/next", { departmentId });
    return response.data;
  },
);

/**
 * Departmandaki sıra bekleyenleri veya şube çalışanının in-progress işlemlerini getir.
 * @param {number|object} params - departmentId veya { departmentId, institutionStaffId?, status? }
 * @param {string} [params.status] - "pending" (bekleyenler) veya "in_progress" (çalışanın bitmemiş işlemleri)
 */
export const fetchDepartmentQueue = createAsyncThunk(
  "appointment/fetchDepartmentQueue",
  async (params) => {
    const departmentId =
      typeof params === "object" ? params.departmentId : params;
    const institutionStaffId =
      typeof params === "object" ? params.institutionStaffId : undefined;
    const status =
      typeof params === "object" ? (params.status ?? "pending") : "pending";

    const queryParams = { departmentId, status };
    if (institutionStaffId != null) {
      queryParams.institutionStaffId = institutionStaffId;
    }

    const response = await api.get("/api/appointments", {
      params: queryParams,
    });
    return response.data;
  },
);

/** Sırayı tamamla - PATCH /api/appointments/:appointmentId/complete */
export const completeAppointment = createAsyncThunk(
  "appointment/completeAppointment",
  async ({ appointmentId, notes }, { rejectWithValue }) => {
    try {
      const body =
        notes != null && notes.trim() !== "" ? { notes: notes.trim() } : {};
      const response = await api.patch(
        `/api/appointments/${appointmentId}/complete`,
        body,
      );
      return response.data;
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      return rejectWithValue(message);
    }
  },
);

const initialState = {
  currentAppointment: null,
  queue: [],
  queueLoading: false,
  queueError: null,
  inProgressList: [],
  inProgressLoading: false,
  inProgressError: null,
  acceptNextLoading: false,
  acceptNextError: null,
  completeLoading: false,
  completeError: null,
};

const appointmentSlice = createSlice({
  name: "appointment",
  initialState,
  reducers: {
    clearCurrentAppointment: (state) => {
      state.currentAppointment = null;
    },
    clearQueue: (state) => {
      state.queue = [];
      state.queueError = null;
    },
    clearInProgress: (state) => {
      state.inProgressList = [];
      state.inProgressError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(acceptNext.pending, (state) => {
        state.acceptNextLoading = true;
        state.acceptNextError = null;
      })
      .addCase(acceptNext.fulfilled, (state, action) => {
        state.acceptNextLoading = false;
        const newAppointment = action.payload?.data?.appointment ?? null;
        state.currentAppointment = newAppointment;
        if (newAppointment) {
          state.inProgressList = [
            newAppointment,
            ...state.inProgressList.filter((a) => a.id !== newAppointment.id),
          ];
        }
        state.acceptNextError = null;
      })
      .addCase(acceptNext.rejected, (state, action) => {
        state.acceptNextLoading = false;
        state.currentAppointment = null;
        state.acceptNextError = action.error.message;
      })
      .addCase(fetchDepartmentQueue.pending, (state, action) => {
        const isInProgress =
          typeof action.meta?.arg === "object" &&
          action.meta.arg.status === "in_progress";
        if (isInProgress) {
          state.inProgressLoading = true;
          state.inProgressError = null;
        } else {
          state.queueLoading = true;
          state.queueError = null;
        }
      })
      .addCase(fetchDepartmentQueue.fulfilled, (state, action) => {
        const appointments =
          action.payload?.data?.appointments ?? action.payload?.data ?? [];
        const isInProgress =
          typeof action.meta?.arg === "object" &&
          action.meta.arg.status === "in_progress";

        if (isInProgress) {
          state.inProgressLoading = false;
          state.inProgressList = Array.isArray(appointments) ? appointments : [];
          state.currentAppointment = state.inProgressList[0] ?? null;
          state.inProgressError = null;
        } else {
          state.queueLoading = false;
          state.queue = Array.isArray(appointments) ? appointments : [];
          state.queueError = null;
        }
      })
      .addCase(fetchDepartmentQueue.rejected, (state, action) => {
        const isInProgress =
          typeof action.meta?.arg === "object" &&
          action.meta.arg.status === "in_progress";
        if (isInProgress) {
          state.inProgressLoading = false;
          state.inProgressList = [];
          state.inProgressError = action.error.message;
        } else {
          state.queueLoading = false;
          state.queue = [];
          state.queueError = action.error.message;
        }
      })
      .addCase(completeAppointment.pending, (state) => {
        state.completeLoading = true;
        state.completeError = null;
      })
      .addCase(completeAppointment.fulfilled, (state, action) => {
        state.completeLoading = false;
        const completedId = action.meta?.arg?.appointmentId;
        if (completedId) {
          state.inProgressList = state.inProgressList.filter(
            (a) => a.id !== completedId,
          );
        }
        state.currentAppointment = state.inProgressList[0] ?? null;
        state.completeError = null;
      })
      .addCase(completeAppointment.rejected, (state, action) => {
        state.completeLoading = false;
        state.completeError = action.payload || action.error.message;
      });
  },
});

export const {
  clearCurrentAppointment,
  clearQueue,
  clearInProgress,
} = appointmentSlice.actions;
export default appointmentSlice.reducer;
